import { useState, type ReactNode } from "react";
import { httpsCallable } from "firebase/functions";

import { functions } from "../FirebaseConfig";
import type { FlightNotamItem, FlightNotams, FlightWeather, NotamFuente, NotamGrupo, NotamSeveridad, WeatherSeveridad } from "../lib/types";

/**
 * NOTAMs del vuelo — botón en la ficha del vuelo que despliega el resumen
 * en lenguaje simple guardado en `Vuelos/{id}.notams` (lo escribe la
 * función `getFlightNotams`, ver functions/notams.js). "Actualizar" fuerza
 * una consulta nueva al IFIS de la DGAC; el doc se refresca solo por la
 * suscripción del padre, así que acá no se guarda el resultado.
 */
const GROUP_ORDER: NotamGrupo[] = ["origen", "ruta", "destino", "otro"];
const GROUP_LABEL: Record<NotamGrupo, string> = {
  origen: "Origen",
  ruta: "En ruta",
  destino: "Destino",
  otro: "Otro aeródromo",
};
const SEVERITY_DOT: Record<NotamSeveridad, string> = {
  afecta: "bg-red-600",
  precaucion: "bg-amber-500",
  info: "bg-slate-400",
};
const SEVERITY_LABEL: Record<NotamSeveridad, string> = {
  afecta: "Afecta la operación",
  precaucion: "Precaución",
  info: "Informativo",
};

function ifisUrl(icao: string): string {
  return `https://aipchile.dgac.gob.cl/notam?designador=${encodeURIComponent(icao)}`;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (!Number.isFinite(min) || min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} días`;
}

const WX_DOT: Record<WeatherSeveridad, string> = { normal: "bg-green-600", atencion: "bg-amber-500", adverso: "bg-red-600" };
const WX_LABEL: Record<WeatherSeveridad, string> = { normal: "Sin factores relevantes", atencion: "Atención", adverso: "Condiciones adversas" };

/** "actualizado hace 2 h", o ambas fechas si NOTAMs y Clima difieren en más de 10 min. */
function updatedLabel(notams: FlightNotams, clima: FlightWeather | null | undefined): string {
  const n = notams.updatedAt.toDate().getTime();
  const c = clima?.updatedAt?.toDate?.().getTime();
  if (c == null || Math.abs(n - c) < 10 * 60_000) {
    return `actualizado ${relativeTime(new Date(Math.max(n, c ?? n)).toISOString())}`;
  }
  return `NOTAMs ${relativeTime(new Date(n).toISOString())} · Clima ${relativeTime(new Date(c).toISOString())}`;
}

export function NotamsPanel({
  flightId,
  notams,
  clima,
  icaos,
}: {
  flightId: string;
  notams: FlightNotams | null | undefined;
  clima?: FlightWeather | null;
  /** Aeródromos del vuelo, para los links al IFIS. */
  icaos: string[];
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = notams?.items ?? [];
  const nAfecta = items.filter((it) => it.severidad === "afecta").length;

  async function generate(refresh: boolean) {
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable<{ flightId: string; refresh: boolean }, { ok: boolean }>(
        functions,
        "getFlightNotams"
      );
      await call({ flightId, refresh });
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message || "No se pudieron obtener los NOTAMs.");
    } finally {
      setBusy(false);
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !notams && !busy) void generate(false);
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-slate-900">Meteo y NOTAMs del vuelo</h2>
          <p className="text-xs text-slate-500">
            {notams
              ? `${items.length} NOTAMs relevantes${nAfecta ? ` · ${nAfecta} afectan la operación` : ""}${clima ? ` · Clima: ${WX_LABEL[clima.severidad] ?? clima.severidad}` : ""} · ${updatedLabel(notams, clima)}`
              : "Clima esperado y NOTAMs del día del vuelo, en lenguaje simple"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {open && notams ? (
            <button
              type="button"
              onClick={() => void generate(true)}
              disabled={busy}
              title="Volver a consultar el IFIS de la DGAC y regenerar el resumen"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy ? "Actualizando…" : "↻ Actualizar"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={toggle}
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium " +
              (open
                ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                : "bg-brand-700 text-white hover:bg-brand-900")
            }
          >
            {open ? "Ocultar" : notams ? "Ver Meteo y NOTAMs" : "Revisar Meteo y NOTAMs"}
          </button>
        </div>
      </div>

      {open ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          {busy && !notams ? (
            <p className="text-sm text-slate-600">
              Consultando IFIS, TAF/METAR/SIGMET y pronóstico, y resumiendo con Claude… suele tomar entre 2 y 3 minutos.
            </p>
          ) : null}
          {clima ? <WeatherBody clima={clima} busy={busy} /> : null}
          {notams ? (
            <h3 className="mt-4 mb-1 border-t border-slate-100 pt-3 text-sm font-medium text-slate-900">NOTAMs</h3>
          ) : null}
          {error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}{" "}
              Revisa los NOTAMs en el IFIS:{" "}
              {icaos.map((i, k) => (
                <span key={i}>
                  {k ? " · " : ""}
                  <a href={ifisUrl(i)} target="_blank" rel="noreferrer" className="underline">
                    {i}
                  </a>
                </span>
              ))}
            </p>
          ) : null}
          {notams ? <NotamsBody notams={notams} icaos={icaos} busy={busy} /> : null}
        </div>
      ) : null}
    </section>
  );
}

function WeatherBody({ clima, busy }: { clima: FlightWeather; busy: boolean }) {
  const [showRaw, setShowRaw] = useState(false);
  const Row = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 py-1.5 text-sm">
      <span className="text-xs text-slate-500 pt-0.5 whitespace-nowrap">{label}</span>
      <div className="text-slate-800">{children}</div>
    </div>
  );
  const Ad = ({ a, rol }: { a: FlightWeather["origen"]; rol: string }) =>
    a ? (
      <Row label={`${rol} · ${a.icao}`}>
        {a.clima ? <p>{a.clima}</p> : null}
        {a.taf ? <p className="text-slate-700">{a.taf}</p> : null}
        {a.tafNota ? <p className="text-[11px] text-slate-400">{a.tafNota}</p> : null}
      </Row>
    ) : null;
  const f = clima.fuentes;
  return (
    <div className={busy ? "opacity-60" : ""}>
      <h3 className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-900">
        <span title={WX_LABEL[clima.severidad]} className={`inline-block h-2.5 w-2.5 rounded-full ${WX_DOT[clima.severidad] ?? "bg-slate-400"}`} />
        Clima esperado
        <span className="text-[11px] font-normal text-slate-400">
          salida {clima.salidaLocal} · llegada {clima.llegadaLocal}
        </span>
      </h3>
      {clima.resumen ? <p className="text-sm text-slate-900">{clima.resumen}</p> : null}
      <div className="mt-1 divide-y divide-dashed divide-slate-100">
        <Row label="Avisos">
          {clima.avisos?.length ? (
            <ul className="list-disc space-y-0.5 pl-4">
              {clima.avisos.map((a, k) => (
                <li key={k}>
                  {a.texto} <span className="text-[11px] text-slate-400">{[a.referencia, a.vigencia].filter(Boolean).join(" · ")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-slate-500">Sin SIGMET vigentes en la ruta.</span>
          )}
        </Row>
        <Ad a={clima.origen} rol="Salida" />
        <Row label="En ruta">
          {clima.ruta?.vientoCrucero ? <p>{clima.ruta.vientoCrucero}</p> : null}
          {clima.ruta?.condiciones ? <p>{clima.ruta.condiciones}</p> : null}
          {clima.ruta?.riesgos?.length ? (
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-amber-800">
              {clima.ruta.riesgos.map((r, k) => (
                <li key={k}>{r}</li>
              ))}
            </ul>
          ) : null}
        </Row>
        <Ad a={clima.destino} rol="Llegada" />
      </div>
      {f ? (
        <div className="mt-1">
          <button type="button" onClick={() => setShowRaw((v) => !v)} className="text-[11px] text-brand-700 hover:underline">
            {showRaw ? "▴ ocultar TAF / METAR / SIGMET originales" : "▾ ver TAF / METAR / SIGMET originales"}
          </button>
          {showRaw ? (
            <div className="mt-1 space-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[12px] leading-snug text-slate-800">
              {f.sigmets.map((s, k) => (
                <p key={`s${k}`} className="whitespace-pre-wrap">{s.raw}</p>
              ))}
              {[f.taf.origen, f.taf.destino].map((t, k) =>
                t ? (
                  <p key={`t${k}`} className="whitespace-pre-wrap">
                    {t.raw} <span className="text-slate-400">({t.station} a {t.distNm} NM{t.cubreVuelo ? "" : ", no cubre la hora del vuelo"})</span>
                  </p>
                ) : null
              )}
              {f.metar.map((m, k) => (
                <p key={`m${k}`} className="whitespace-pre-wrap">
                  {m.raw}{" "}
                  {m.distNm ? <span className="text-slate-400">({m.station} a {m.distNm} NM)</span> : null}
                </p>
              ))}
              {!f.sigmets.length && !f.taf.origen && !f.taf.destino && !f.metar.length ? <p className="text-slate-500">Sin mensajes crudos disponibles.</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="mt-2 text-[11px] text-slate-500">
        Fuentes: NOAA Aviation Weather Center (TAF, METAR, SIGMET) y Open-Meteo. No reemplaza el briefing meteorológico oficial.
      </p>
    </div>
  );
}

function NotamsBody({
  notams,
  icaos,
  busy,
}: {
  notams: FlightNotams;
  icaos: string[];
  busy: boolean;
}) {
  const groups = GROUP_ORDER.map((g) => [g, notams.items.filter((it) => it.grupo === g)] as const).filter(
    ([, L]) => L.length
  );
  return (
    <div className={busy ? "opacity-60" : ""}>
      {notams.warnings && notams.warnings.length ? (
        <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
          {notams.warnings.join(" ")} Resultado parcial: conviene actualizar más tarde.
        </p>
      ) : null}
      {notams.foreignAerodromes && notams.foreignAerodromes.length ? (
        <p className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
          Este vuelo incluye aeródromos fuera de Chile ({notams.foreignAerodromes.join(", ")}). El sistema solo
          revisa NOTAMs de Chile: el tramo y los aeródromos en el extranjero NO están cubiertos. Revísalos con la
          fuente oficial de ese país.
        </p>
      ) : null}
      {notams.resumen ? <p className="text-sm text-slate-900">{notams.resumen}</p> : null}
      {notams.items.length === 0 ? (
        <p className="mt-2 text-sm italic text-slate-500">Sin NOTAMs relevantes para este vuelo.</p>
      ) : null}
      {groups.map(([g, L]) => {
        const icao = g === "ruta" ? null : L.find((it) => it.icao)?.icao;
        return (
          <div key={g} className="mt-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {GROUP_LABEL[g]}
              {icao ? ` · ${icao}` : ""}
            </h3>
            <ul className="mt-1 divide-y divide-dashed divide-slate-100">
              {L.map((it, k) => (
                <NotamItem key={`${g}-${k}`} item={it} fuentes={notams.fuentes} />
              ))}
            </ul>
          </div>
        );
      })}
      <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-slate-600">
        Resumen automático a partir del IFIS de la DGAC ({notams.candidatos} NOTAMs analizados
        {notams.descartados ? `, ${notams.descartados} descartados sin efecto para este vuelo` : ""}). No reemplaza el
        briefing oficial del piloto. NOTAMs completos:{" "}
        {icaos.map((i, k) => (
          <span key={i}>
            {k ? " · " : ""}
            <a href={ifisUrl(i)} target="_blank" rel="noreferrer" className="text-brand-700 underline">
              {i}
            </a>
          </span>
        ))}
        .
      </p>
    </div>
  );
}

function NotamItem({
  item,
  fuentes,
}: {
  item: FlightNotamItem;
  fuentes: Record<string, NotamFuente> | undefined;
}) {
  const [open, setOpen] = useState(false);
  const showHorario = item.horario !== "durante" && item.horario !== "todo_el_dia" && item.notaHorario;
  const sources = item.referencias.map((id) => [id, fuentes?.[id]] as const);
  const hasSources = sources.some(([, f]) => f);
  return (
    <li className="py-2 text-sm leading-snug">
      <button
        type="button"
        onClick={() => hasSources && setOpen((v) => !v)}
        disabled={!hasSources}
        title={hasSources ? (open ? "Ocultar NOTAM original" : "Ver NOTAM original") : undefined}
        className={
          "flex w-full gap-2.5 text-left " +
          (hasSources ? "cursor-pointer rounded-md hover:bg-slate-50" : "cursor-default")
        }
      >
        <span
          title={SEVERITY_LABEL[item.severidad]}
          className={`mt-1.5 inline-block h-2.5 w-2.5 flex-none rounded-full ${SEVERITY_DOT[item.severidad]}`}
        />
        <div className="min-w-0 flex-1">
          {item.titulo ? <p className="font-medium text-slate-900">{item.titulo}</p> : null}
          <p className={item.titulo ? "text-slate-700" : "text-slate-900"}>{item.texto}</p>
          {item.detalles && item.detalles.length ? (
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-slate-700">
              {item.detalles.map((d, k) => (
                <li key={k}>{d}</li>
              ))}
            </ul>
          ) : null}
          <p className="mt-0.5 text-[11px] text-slate-400">
            {item.referencias.join(", ")}
            {item.vigencia ? ` · ${item.vigencia}` : ""}
            {showHorario ? <span className="text-amber-700"> · {item.notaHorario}</span> : null}
            {hasSources ? (
              <span className="ml-1 text-brand-700">{open ? "▴ ocultar original" : "▾ ver original"}</span>
            ) : null}
          </p>
        </div>
      </button>
      {open ? (
        <div className="ml-5 mt-1.5 space-y-2">
          {sources.map(([id, f]) =>
            f ? (
              <div key={id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] text-slate-500">
                  <span className="font-mono font-medium text-slate-700">{id}</span>
                  {f.aerodromo ? ` · ${f.aerodromo}` : ""}
                  {f.codigo ? ` · ${f.codigo}` : ""}
                  {f.vigencia ? ` · ${f.vigencia}` : ""}
                  {f.ubicacion ? ` · ${f.ubicacion}` : ""}
                </p>
                <p className="mt-1 whitespace-pre-wrap font-mono text-[12px] leading-snug text-slate-800">
                  {f.q ? `Q) ${f.q}\n` : ""}
                  {f.d ? `D) ${f.d}\n` : ""}
                  {`E) ${f.e}`}
                  {f.f ? `\nF) ${f.f}` : ""}
                  {f.g ? `  G) ${f.g}` : ""}
                </p>
              </div>
            ) : (
              <p key={id} className="text-[11px] text-slate-400">
                {id}: texto original no guardado (resumen generado con una versión anterior).
              </p>
            )
          )}
        </div>
      ) : null}
    </li>
  );
}
